import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Eye, EyeOff, Sparkles, ArrowRight, Github, Chrome } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { GlowBackdrop, GlassCard, GradientButton, GhostButton } from "@/components/primitives";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("admin@sentinelai.io");
  const [password, setPassword] = useState("Sentinel@2026");
  const [show, setShow] = useState(false);
  const [remember, setRemember] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      await login(email.trim(), password);
      nav("/app/dashboard");
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Login failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen relative grid place-items-center px-6 py-14">
      <GlowBackdrop intense />
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 mb-8 justify-center" data-testid="auth-logo">
          <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-cyan-400 via-blue-500 to-violet-500 grid place-items-center"><Sparkles className="h-4 w-4 text-white" /></div>
          <div className="text-sm font-bold tracking-tight">SentinelAI</div>
        </Link>
        <GlassCard className="p-8">
          <h1 className="text-2xl font-bold">Welcome back</h1>
          <p className="text-sm text-slate-400 mt-1">Sign in to your operations workspace.</p>
          <form onSubmit={submit} className="mt-6 space-y-4">
            <label className="block">
              <div className="text-xs uppercase tracking-widest text-slate-500 mb-1.5">Email</div>
              <input
                data-testid="login-email"
                type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-white/[0.04] border border-white/10 focus:border-cyan-400/50 focus:outline-none rounded-xl px-4 py-2.5 text-sm placeholder:text-slate-500"
                placeholder="you@company.com"
              />
            </label>
            <label className="block">
              <div className="text-xs uppercase tracking-widest text-slate-500 mb-1.5 flex justify-between">
                <span>Password</span>
                <Link to="#" className="text-cyan-300 normal-case tracking-normal">Forgot?</Link>
              </div>
              <div className="relative">
                <input
                  data-testid="login-password"
                  type={show ? "text" : "password"} required value={password} onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-white/[0.04] border border-white/10 focus:border-cyan-400/50 focus:outline-none rounded-xl px-4 py-2.5 pr-10 text-sm"
                  placeholder="••••••••"
                />
                <button type="button" data-testid="login-show-password" onClick={() => setShow((s) => !s)} className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-slate-400 hover:text-white">
                  {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-400">
              <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} className="accent-cyan-400" /> Remember me
            </label>

            {err && <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2" data-testid="login-error">{err}</div>}

            <GradientButton type="submit" disabled={busy} data-testid="login-submit" className="w-full py-3">
              {busy ? "Signing in…" : (<>Sign in <ArrowRight className="h-4 w-4" /></>)}
            </GradientButton>
          </form>

          <div className="my-5 flex items-center gap-3 text-[11px] uppercase tracking-widest text-slate-500">
            <div className="flex-1 h-px bg-white/5" /> or continue with <div className="flex-1 h-px bg-white/5" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <GhostButton type="button" data-testid="login-google"><Chrome className="h-4 w-4" /> Google</GhostButton>
            <GhostButton type="button" data-testid="login-github"><Github className="h-4 w-4" /> GitHub</GhostButton>
          </div>

          <div className="mt-6 text-center text-sm text-slate-400">
            Don&apos;t have an account? <Link to="/register" className="text-cyan-300" data-testid="login-register-link">Create one</Link>
          </div>
        </GlassCard>
        <div className="mt-3 text-center text-[11px] text-slate-500 font-mono">
          Demo · admin@sentinelai.io / Sentinel@2026
        </div>
        <div className="mt-3 text-center text-[10px] uppercase tracking-widest text-slate-500 font-mono">
          Built by <span className="gradient-text font-bold tracking-wider">Team Hack</span> · Anu · Aravind · Narendar · Sahil
        </div>
      </motion.div>
    </div>
  );
}
